import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Button primitive — mirrors `.ez-btn` from design/app-tokens.css.
 * - `primary`   = Volt + ink text + volt-glow shadow
 * - `secondary` = white surface + line-strong border
 * - `ghost`     = transparent
 * - `danger`    = red
 *
 * Sizes follow the design: 30 / 36 / 44px tall.
 * `iconOnly` reduces the width to a square so 16-20px icons sit centered.
 */
const buttonVariants = cva(
  // Base — reproduces .ez-btn (display:inline-flex, gap:8, transition, etc.).
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium leading-none transition-all duration-100 ease-out border border-transparent rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-volt-glow focus-visible:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
  {
    variants: {
      variant: {
        primary:
          'bg-volt text-volt-ink border-volt shadow-volt hover:bg-[#3dca6f] hover:border-[#3dca6f] active:translate-y-[0.5px]',
        secondary:
          'bg-surface text-text border-line-strong hover:bg-surface-2 hover:border-text-3',
        ghost:
          'bg-transparent text-text-2 border-transparent hover:bg-surface-3 hover:text-text',
        danger:
          'bg-danger text-white border-danger hover:bg-[#b91c1c] hover:border-[#b91c1c]',
      },
      size: {
        sm: 'h-[30px] px-2.5 text-[13px]',
        default: 'h-9 px-3.5 text-sm',
        lg: 'h-11 px-5 text-[15px]',
      },
      iconOnly: {
        true: 'p-0',
        false: '',
      },
    },
    compoundVariants: [
      { iconOnly: true, size: 'sm', class: 'w-[30px]' },
      { iconOnly: true, size: 'default', class: 'w-9' },
      { iconOnly: true, size: 'lg', class: 'w-11' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      iconOnly: false,
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, iconOnly, type = 'button', ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(buttonVariants({ variant, size, iconOnly }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
